import {
  Directive,
  ElementRef,
  Renderer2,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';

export interface SparklinePoint {
  date: Date;
  value: number;
}

/**
 * `.groove-sparkline` — element tier.
 *
 * Renders a path-line into the host `<svg>`. Computes the SVG path-d from
 * `[points]`, scaled to the SVG viewBox.
 *
 * Reduced-motion-as-default: the draw animation lives inside
 * `@media (prefers-reduced-motion: no-preference)` in the CSS, never in the
 * default declaration, so motion is opt-in by user preference.
 */
@Directive({
  selector: '[grooveSparkline]',
  standalone: true,
  host: {
    class: 'groove-sparkline',
    role: 'img',
    '[attr.data-tone]': 'toneAttr()',
    '[attr.data-state]': 'stateAttr()',
  },
})
export class GrooveSparklineDirective {
  readonly points = input<SparklinePoint[]>([]);
  readonly tone = input<'default' | 'accent' | 'muted' | undefined>(undefined);

  // Omit default-value attrs to keep the DOM clean.
  protected readonly toneAttr = computed(() => {
    const t = this.tone();
    return t === 'default' || t === undefined ? null : t;
  });

  protected readonly stateAttr = computed<'empty' | 'ready'>(() =>
    this.points().length === 0 ? 'empty' : 'ready',
  );

  private readonly host = inject<ElementRef<SVGSVGElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);

  constructor() {
    effect(() => {
      this.renderPath(this.points());
    });
  }

  private renderPath(points: SparklinePoint[]): void {
    const svg = this.host.nativeElement;
    // Clear existing paths
    svg.querySelectorAll('path.groove-sparkline-path').forEach((p) => {
      p.remove();
    });

    if (points.length === 0) {
      return;
    }

    const vbWidth = 100;
    const vbHeight = 30;
    const padding = 2;
    const minV = Math.min(...points.map((p) => p.value));
    const maxV = Math.max(...points.map((p) => p.value));
    const range = maxV - minV || 1;
    const stepX = (vbWidth - 2 * padding) / Math.max(points.length - 1, 1);
    const d = points
      .map((p, i) => {
        const x = padding + i * stepX;
        const y =
          vbHeight -
          padding -
          ((p.value - minV) / range) * (vbHeight - 2 * padding);
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
      })
      .join(' ');

    const approxLength = Math.round(points.length * stepX * 1.2);

    // Renderer2.createElement returns `any`; cast to SVGPathElement which
    // is the concrete type produced by the SVG namespace argument.
    const path = this.renderer.createElement(
      'path',
      'http://www.w3.org/2000/svg',
    ) as SVGPathElement;
    this.renderer.addClass(path, 'groove-sparkline-path');
    this.renderer.setAttribute(path, 'd', d);
    this.renderer.setAttribute(path, 'fill', 'none');
    this.renderer.setStyle(
      path,
      '--_sparkline-path-length',
      String(approxLength),
    );
    this.renderer.appendChild(svg, path);
  }
}
